import React, { useEffect, useReducer, useState } from 'react';
import { connect, MapStateToProps } from 'react-redux';

import { appDispatch, RootState } from 'store/store';

import * as ExcelJS from 'exceljs';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';

import messages from './Messages';
import Button from 'components/Button';
import Input from '../../../../Input/Input';
import Select from '../../../../Select';
import { getTags } from './GeneralQuestion/GeneralQuestionEditor';
import { IOption } from '../../../../Select/Select';
import cloneDeep from 'lodash/cloneDeep';
import notify, { notifyError } from '../../../../Notify/Notify';
import { IDContainer, SelectedSectionItem } from './styles';
import {
  Field,
  IProviderItemView,
  IProviderItemViewWithData,
  ITags,
  ITagView,
} from 'store/providers/providerTypes';
import {
  IChoiceView,
  IIndicatorQuestionView,
  IMultipleChoiceQuestionView,
  IQuestionsView,
  ISurveyItem,
  ISurveyItemWithData,
  ISurveyView,
  ISurveyViewWithData,
  PeriodType,
  SURVEY_ANALYSE_LINK_DESC_NAME,
} from '../../../../../store/survey/surveyTypes';
import {
  addQuestion,
  generateReports,
  requireSurvey,
  updateQuestion,
  updateSurvey,
} from '../../../../../store/survey/surveySlice';
import { convertTagIDs } from './MultiChoiceQuestion/MultiChoiceFields/TagChoiceEditor';
import { lightGray, orange, red } from '../../../../../utils/style/colors';
import { ICompany } from '../../../../../store/companies/companiesTypes';
import { v1 as uuid } from 'uuid';
import Icon from '../../../../Icon';
import {
  createNewItemLink,
  item_createNewSurvey,
  requireDocument,
} from '../../../../../store/providers/providerSlice';
import Collapsible from '../../../../Collapsible';
import { setIn } from 'formik';
import i18n, { languageDirection } from '../../../../../i18n';

interface IStateToProps {
  tags: ITags['data'];
  templateSurvey?: ISurveyView;
}

interface IReducerState {
  title: string;
  numerator: string;
  numeratorUnit: string;
  denominator: string;
  denominatorUnit: string;
  unit: string;
}

interface IOtherSplitState {
  title: string;
  choices: IChoiceView[];
}

const unitOptions: IOption[] = [
  'درصد',
  'تعداد',
  'ساعت',
  'دقیقه',
  'نفر ساعت',
  'میلی گرم',
  'لیتر',
  'درجه',
  'روز',
  'نفر',
  'تومان',
  'ریال',
  'میلیون تومان',
  'میلیون ریال',
  'تخت روز',
  'PPM',
  'دسی بل',
  'لوکس',
  'مترمکعب',
  'میلی لیتر',
  'میانگین',
  'امتیاز',
].map((it) => ({
  value: it,
  label: it,
}));

const indicatorUnitOptions: IOption[] = [
  {
    value: 'percent',
    label: 'درصد',
  },
  {
    value: 'fraction',
    label: 'نسبت',
  },
];

interface IReducerAction {
  type:
    | 'title'
    | 'numerator'
    | 'numeratorUnit'
    | 'unit'
    | 'denominator'
    | 'denominatorUnit';
  payload: string;
}

interface IOtherSplitAction {
  type: 'title' | 'add' | 'update' | 'delete';
  payload: { value: string; text?: string };
}

const reducer = (state: IReducerState, action: IReducerAction) => {
  const res = {
    ...state,
    [action.type]: action.payload,
  };
  if (res.numeratorUnit !== res.denominatorUnit) res.unit = '';
  return res;
};

const otherSplitreducer = (
  state: IOtherSplitState,
  action: IOtherSplitAction
) => {
  switch (action.type) {
    case 'title':
      return { ...state, title: action.payload.value };
    case 'add':
      return {
        ...state,
        choices: [
          ...state.choices,
          { value: action.payload.value, text: '', score: null },
        ],
      };
    case 'update':
      return {
        ...state,
        choices: [
          ...state.choices.map((item) => {
            return item.value === action.payload.value
              ? {
                  value: item.value,
                  text: action.payload.text || '',
                  score: null,
                }
              : item;
          }),
        ],
      };
    case 'delete':
      return {
        ...state,
        choices: [
          ...state.choices.filter((item) => {
            return item.value !== action.payload.value;
          }),
        ],
      };
    default:
      return state;
  }
};

// TODO: define types with "typeof" for dispatches
interface IDispatchToProps {}
interface IOwnProps {
  item: IProviderItemViewWithData;
  updateItem: (item: IProviderItemView) => Promise<any>;
  survey: ISurveyViewWithData;
  tags: { [tagId: string]: ITagView };
  company: ICompany;
  analyseItem: ISurveyItemWithData;
  analyseSurvey: ISurveyViewWithData;
  templateItem: ISurveyItemWithData;
  readonly?: boolean;
}
type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

export function getEvaluatedMetric(numerator: string, denominator?: string) {
  if (!denominator) return numerator;
  if (numerator === denominator) return i18n.t('percent', { ns: 'survey' });
  return `${numerator} \\ ${denominator}`;
}

interface IUnableToImport {
  [key: number]: string;
}

const Identification: React.FC<IComponentProps> = ({
  item,
  updateItem,
  tags,
  readonly,
  survey,
  company,
  analyseItem,
  analyseSurvey,
  templateItem,
  templateSurvey,
}) => {
  const questionIncludeOptions: IOption[] = [
    {
      label: i18n.t('analysisBaseOnSections', { ns: 'survey' }),
      value: 'default',
    },
    {
      label: i18n.t('analysisBaseOnSectionsAndTotal', { ns: 'survey' }),
      value: 'both',
    },
    {
      label: i18n.t('analysisBaseOnTotal', { ns: 'survey' }),
      value: 'total',
    },
  ];

  const periodType = [
    { value: 'none', label: i18n.t('disabled', { ns: 'survey' }) as string },
    { value: 'day', label: i18n.t('day', { ns: 'survey' }) as string },
    { value: 'week', label: i18n.t('week', { ns: 'survey' }) as string },
    { value: 'month', label: i18n.t('month', { ns: 'survey' }) as string },
    { value: 'season', label: i18n.t('season', { ns: 'survey' }) as string },
    {
      value: 'twoSeason',
      label: i18n.t('twoSeason', { ns: 'survey' }) as string,
    },
    { value: 'year', label: i18n.t('year', { ns: 'survey' }) as string },
  ];

  const [currentTags, setCurrentTags] = useState(item.data.tags);
  const [currentIdValues, setCurrentIdValues] = useState(
    cloneDeep(item.data.identifierValues) || {}
  );
  const foundIDTagsId = item.data.tags
    .filter((it) => tags[it.id]?.type === 'Identification')
    .map((it) => {
      return it.id;
    });

  useEffect(() => {
    if (!templateSurvey && !analyseItem && templateItem) {
      appDispatch(requireSurvey({ providerSurveyItemData: templateItem.data }));
    }
  }, [templateItem]);

  const tagList: ITagView[] = Object.values(tags);
  const idParentList = tagList
    .filter(
      (it: ITagView) => foundIDTagsId.includes(it.parentId || -1) && !it.deleted
    )
    .sort((a, b) => {
      return (a.order || 0) - (b.order || 0);
    })
    .map((it) => it.id);
  const idValueList: { [key: number]: number[] } = {};
  const allPossibleIdValues = new Set<number>();
  tagList
    .sort(
      (a, b) => (a.order ?? (a.id as number)) - (b.order ?? (b.id as number))
    )
    .forEach((it) => {
      if (it.deleted) return;
      if (idParentList.includes(it.parentId || -1)) {
        if (!idValueList[it.parentId!]) idValueList[it.parentId!] = [];
        idValueList[it.parentId!].push(Number(it.id));
        allPossibleIdValues.add(Number(it.id)!);
      }
    });

  const findDiff = (base: IOption[], target: IOption[]) => {
    const baseIds = base.map((it) => it.value as number);
    const baseIdSet = new Set(baseIds);
    const targetIds = target.map((it) => it.value as number);
    const targetIdSet = new Set(targetIds);
    const toAdd = new Set(targetIds.filter((x) => !baseIdSet.has(x)));
    const toDelete = new Set(baseIds.filter((x) => !targetIdSet.has(x)));
    return [toAdd, toDelete];
  };
  const updateTag = (toAdd: Set<number>, toRemove: Set<number>) => {
    const addedTag = Array.from(toAdd).map((id) => tags[id]);
    setCurrentTags((prevVal) => [
      ...prevVal.filter((it) => !toRemove.has(Number(it.id))),
      ...addedTag,
    ]);
  };
  const handleUpdateTag = (base: IOption[]) => (values: IOption[]) => {
    const [toAdd, toRemove] = findDiff(base, values);
    updateTag(toAdd, toRemove);
  };
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [unableToImport, setUnableToImport] = useState<IUnableToImport>({});
  const { t } = useTranslation('survey');

  // define period type
  const viewingVersion = survey.viewingVersion!;
  const [selectedPeriod, setSelectedPeriod] = useState(
    survey?.data.versions?.[viewingVersion]?.periodType.toString() || 'none'
  );
  //

  const oddIndicator =
    Object.values(
      survey?.data.versions?.[viewingVersion]?.questionMap || {}
    )?.filter((item) => item.type === 'indicator' && !item.deleted)?.length >
      1 ||
    (survey?.data.versions?.[viewingVersion]?.questionList?.filter(
      (item) => item.type === 'multipleChoice' && item.useTags && !item.deleted
    )?.length ?? 0) > 1 ||
    (survey?.data.versions?.[viewingVersion]?.questionList?.filter(
      (item) => item.type === 'multipleChoice' && !item.useTags && !item.deleted
    )?.length ?? 0) > 1;

  // define indicator question
  const indicatorQuestion = Object.values(
    survey?.data.versions?.[viewingVersion]?.questionMap || {}
  )?.find((item) => item.type === 'indicator' && !item.deleted) as
    | IIndicatorQuestionView
    | undefined;

  const initialIndicatorState = {
    title: indicatorQuestion?.title || item.data.name!,
    numerator: indicatorQuestion?.numerator || '',
    numeratorUnit: indicatorQuestion?.numeratorUnit || '',
    denominator: indicatorQuestion?.denominator || '',
    denominatorUnit: indicatorQuestion?.denominatorUnit || '',
    unit: indicatorQuestion?.unit || '',
  };
  const [indicatorState, dispatch] = useReducer(reducer, initialIndicatorState);
  //

  // tagIDs
  const rootDistrictTags = Object.values(company.roles)
    .filter(
      (role) => role.roleType === 'District' && !role.surroundingMeRoles?.length
    )
    .map((el) => el.relatedTagId)
    .filter((el) => Boolean(el));

  const hospitalId = Number(rootDistrictTags?.[0]) ?? messages.hospital;
  const hospitalChildId = getTags(
    tags,
    true,
    rootDistrictTags as number[]
  ).reduce((acc: number[], tag) => {
    return [...acc, +tag.value];
  }, []);

  const [multipleQuestionTag, setMultipleQuestionTag] = useState(() => {
    return survey?.data.versions?.[viewingVersion]?.questionList?.find(
      (item) => item.type === 'multipleChoice' && item.useTags && !item.deleted
    ) as IMultipleChoiceQuestionView | undefined;
  });

  const initialMultipleChoiceTagDeletedStatus =
    multipleQuestionTag?.deleted ?? true;
  const [isMultipleChoiceTagDeleted, setIsMultipleChoiceTagDeleted] = useState(
    initialMultipleChoiceTagDeletedStatus
  );

  const [selectedParentTagIdList, setSelectedParentTagIdList] = useState<
    number[]
  >(() => {
    return multipleQuestionTag?.parentTagChoices || [];
  });
  const [subTags, setSubTags] = useState<number[]>(() => {
    return multipleQuestionTag?.tagChoices || [];
  });
  const [excludeTagChoice, setExcludeTagChoice] = useState<number[]>(() => {
    return multipleQuestionTag?.excludedTagChoices || [];
  });

  // second Type of multipleChoice question
  const otherSpitQuestion = survey?.data.versions?.[
    viewingVersion
  ]?.questionList?.find(
    (item) =>
      item.type === 'multipleChoice' &&
      !item.useTags &&
      item.distinctive &&
      !item.deleted
  ) as IMultipleChoiceQuestionView;

  const initialOtherSplitStatus: IOtherSplitState = {
    title: otherSpitQuestion?.title || '',
    choices: otherSpitQuestion?.choiceList || [],
  };
  const [otherSplitState, otherSplitDispatch] = useReducer(
    otherSplitreducer,
    initialOtherSplitStatus
  );
  const initialOtherSplitQuestionStatus = otherSpitQuestion?.deleted ?? true;
  const [isOtherSplitDeleted, setIsOtherSplitDeleted] = useState(
    initialOtherSplitQuestionStatus
  );

  useEffect(() => {
    const hasDeletedTag = Object.values(
      survey?.data.versions?.[viewingVersion]?.questionMap || {}
    )?.find(
      (question) =>
        question.type === 'multipleChoice' &&
        question.useTags &&
        question.deleted
    ) as IMultipleChoiceQuestionView;
    if (isMultipleChoiceTagDeleted && hasDeletedTag) {
      setMultipleQuestionTag({ ...hasDeletedTag, deleted: false });
    }
  }, [isMultipleChoiceTagDeleted]);

  // Analyse
  const [selectedAnalysedPeriodType, setSelectedAnalysedPeriodType] = useState(
    () => {
      return (
        analyseSurvey?.data?.versions?.[
          viewingVersion
        ]?.periodType.toString() || 'none'
      );
    }
  );
  const analyseMultipleQuestion = analyseSurvey?.data?.versions?.[
    viewingVersion
  ]?.questionList?.find(
    (item) => item.type === 'multipleChoice' && item.useTags
  ) as IMultipleChoiceQuestionView;
  const getCurrentAnalyseSplitValue = () => {
    return !analyseMultipleQuestion
      ? 'total'
      : analyseMultipleQuestion?.tagChoices?.includes(hospitalId)
        ? analyseMultipleQuestion?.tagChoices?.length > 1 ||
          (analyseMultipleQuestion?.parentTagChoices?.length ?? 0) > 0
          ? 'both'
          : 'total'
        : 'default';
  };
  const [selectedAnalysedOption, setSelectedAnalysedOption] = useState<
    'both' | 'total' | 'default'
  >(() => {
    return getCurrentAnalyseSplitValue();
  });

  const createAnalyseSurvey = () => {
    return appDispatch(
      item_createNewSurvey({
        item: {
          ...templateItem.data,
          dadId: item.data.dadId,
          id: undefined as any,
          name: i18n.t('analyse', { ns: 'survey' }) + ' - ' + item.data.name,
          hidden: true,
        },
      })
    ).then(async (response: any) => {
      const surveyData = { ...templateSurvey! };
      const analyseItemObj = response.payload.remoteItem as ISurveyItem;
      surveyData.id = analyseItemObj.surveyId;
      surveyData.name = analyseItemObj.name;
      surveyData.periodType = selectedAnalysedPeriodType as PeriodType;
      await appDispatch(
        updateSurvey({
          surveyData: surveyData,
          providerSurveyItemData: analyseItemObj,
        })
      );
      await appDispatch(
        createNewItemLink({
          sourceItemId: item.data.id,
          destinationItemId: analyseItemObj.id,
          description: SURVEY_ANALYSE_LINK_DESC_NAME,
          tempItemId: null,
          providerId: item.data.providerId,
        })
      );
      for (const { data: q } of Object.values(
        templateSurvey!.questionMap || {}
      ).sort((a, b) => a.data.order - b.data.order)) {
        if (!q.deleted)
          await appDispatch(
            addQuestion({
              questionData: q,
              providerSurveyData: analyseItemObj,
              keepQuestionId: true,
            })
          );
      }
      for (const { data: q } of Object.values(
        survey.data.questionMap || {}
      ).sort((a, b) => a.data.order - b.data.order)) {
        if (
          q.distinctive &&
          !q.deleted &&
          q.type === 'multipleChoice' &&
          q.useTags
        ) {
          const clone = cloneDeep(q) as IMultipleChoiceQuestionView;
          if (selectedAnalysedOption === 'both')
            clone.tagChoices = [...(clone.tagChoices ?? []), hospitalId];
          else if (selectedAnalysedOption === 'total') {
            clone.tagChoices = [hospitalId];
            clone.parentTagChoices = [];
            clone.excludedTagChoices = [];
          }
          await appDispatch(
            addQuestion({
              questionData: clone,
              providerSurveyData: analyseItemObj,
              keepQuestionId: true,
            })
          );
        }
      }
      await appDispatch(
        requireSurvey({
          providerSurveyItemData: analyseItemObj,
          forceReload: true,
        })
      );
      await appDispatch(
        requireDocument({
          documentId: item.data.id,
        })
      );
    });
  };

  const handleOptionListToString = (list: IOption[]) => {
    const accumulatedString = list.reduce((acc, item, itemInd) => {
      if (list.length - 1 === itemInd) return acc + item.label;
      return `${acc + item.label} ${i18n.t('comma', {
        ns: 'companyManagement',
      })}`;
    }, '');

    return accumulatedString;
  };

  const addRowWithStyle = (
    worksheet: ExcelJS.Worksheet,
    col1: string,
    col2: string
  ) => {
    const col = worksheet.addRow({
      col1: col1,
      col2: col2,
    });

    col.getCell(1).alignment = { wrapText: true };
    col.getCell(2).font = {
      bold: true,
      size: 12,
    };
  };

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');
    setExporting(true);

    worksheet.columns = [
      { header: `${t('identificationValue')}`, key: 'col1', width: 80 },
      { header: `${t('identificationTitle')}`, key: 'col2', width: 30 },
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '000000' },
      };
      cell.font = {
        color: { argb: 'FFFFFF' },
        bold: true,
        size: 12,
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    // Initial Configs
    addRowWithStyle(
      worksheet,
      `${indicatorState.title}`,
      `${i18n.t('indicatorQuestion', {
        ns: 'survey',
      })}`
    );
    addRowWithStyle(
      worksheet,
      `${indicatorState.numerator || ''}`,
      `${i18n.t('numeratorQuestion', {
        ns: 'survey',
      })}`
    );
    addRowWithStyle(
      worksheet,
      `${indicatorState.numeratorUnit ? indicatorState.numeratorUnit : ''}`,
      `${i18n.t('numeratorUnit', { ns: 'survey' })}`
    );
    addRowWithStyle(
      worksheet,
      `${indicatorState.denominator || ''}`,
      `${i18n.t('denominatorQuestion', {
        ns: 'survey',
      })}`
    );
    addRowWithStyle(
      worksheet,
      `${indicatorState.denominatorUnit ? indicatorState.denominatorUnit : ''}`,
      `${i18n.t('denominatorUnit', {
        ns: 'survey',
      })}`
    );

    const periodType =
      validPeriodTypes.find(
        (item: IOption) => item.value.toString() === selectedPeriod
      )?.label || '';
    addRowWithStyle(
      worksheet,
      `${periodType}`,
      `${i18n.t('periodType', { ns: 'survey' })}`
    );

    // District configuration
    if (isMultipleChoiceTagDeleted) {
      const selectedSubTags = filteredSubTags
        ? convertTagIDs(filteredSubTags, tags)
        : [];
      const accumulatedSubTags = handleOptionListToString(selectedSubTags);
      addRowWithStyle(
        worksheet,
        `${accumulatedSubTags}`,
        `${i18n.t('indicatorDomain', { ns: 'survey' })}`
      );
    } else {
      const allSubTags = Object.keys(tags)
        .filter((tagId) => selectedParentTagIdList.includes(+tagId))
        .map((tagId) => {
          return { label: tags[tagId].text, value: tagId };
        });
      const accumulatedAllSubTags = handleOptionListToString(allSubTags);
      const selectedSubtags = convertTagIDs(subTags, tags);
      const accumulatedSubTags = handleOptionListToString(selectedSubtags);
      const excludedTagChoice = convertTagIDs(excludeTagChoice, tags) || [];
      const accumulatedExcludedTagChoice =
        handleOptionListToString(excludedTagChoice);

      const finalValue =
        [accumulatedAllSubTags, accumulatedSubTags].join(', ') +
        (accumulatedExcludedTagChoice
          ? (i18n.t('excludedTags', { ns: 'survey' }) as string) +
            accumulatedExcludedTagChoice
          : '');

      addRowWithStyle(
        worksheet,
        `${finalValue}`,
        `${i18n.t('indicatorDomain', { ns: 'survey' })}`
      );
    }

    // analysed period type
    const analysedPeriodType =
      validPeriodTypes.find(
        (item: IOption) => item.value.toString() === selectedAnalysedPeriodType
      )?.label || '';
    addRowWithStyle(
      worksheet,
      `${analysedPeriodType}`,
      `${i18n.t('analysedPeriodType', { ns: 'survey' })}`
    );
    // Pre-defined values ( custom ones )
    idParentList.map((tagId) => {
      const itemValues: string | IOption[] = idValueList[tagId]
        ? currentTags
            .filter((it) => idValueList[tagId].includes(it.id))
            .map((it) => it.text)
            .join(', ')
        : [];
      const accumulatedString = idValueList[tagId]
        ? itemValues
        : currentIdValues[tagId] || '';

      addRowWithStyle(
        worksheet,
        `${accumulatedString || ''}`,
        `${tags[tagId].text}`
      );
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${survey.data.name}.xlsx`);
    setExporting(false);
  };

  const setIdValue = (
    key: number | string,
    val: string | undefined,
    toSave: IProviderItemViewWithData
  ) => {
    currentIdValues[key] = val;
    setCurrentIdValues({ ...currentIdValues });
    toSave.data.identifierValues[key] = val;
  };

  const handleSave = async () => {
    try {
      const toSave = cloneDeep(item);
      toSave.data.identifierValues = currentIdValues;
      toSave.data.tags = currentTags;
      if (indicatorState.title) {
        toSave.data.name = indicatorState.title;
      }
      setSaving(true);
      // Update Item if divide sections were unable
      let updatedQuestionTags: number[] = [...filteredSubTags];
      if (!isMultipleChoiceTagDeleted) {
        // add children of parent tags
        updatedQuestionTags = [...subTags];
        updatedQuestionTags = [
          ...updatedQuestionTags,
          ...Object.values(tags)
            .filter((tag) =>
              selectedParentTagIdList.includes(tag.parentId as number)
            )
            .reduce((acc: number[], tag) => {
              acc.push(+tag.id);
              return acc;
            }, []),
        ];
        updatedQuestionTags = Array.from(new Set(updatedQuestionTags));
        updatedQuestionTags = updatedQuestionTags.filter(
          (tagId) => !excludeTagChoice.includes(tagId)
        );
      }
      const listOfAllSubTagsId = getTags(tags, true, hospitalChildId).reduce(
        (acc: number[], tag) => {
          acc.push(+tag.value);
          return acc;
        },
        []
      );
      toSave.data.tags = Array.from(
        new Set([
          ...Object.values(tags).filter(
            (tag) => updatedQuestionTags.includes(+tag.id) && !tag.deleted
          ),
          ...toSave.data.tags.filter(
            (tag) => !listOfAllSubTagsId.includes(+tag.id) && !tag.deleted
          ),
        ])
      );
      const fillPeriodInIds = idParentList.find(
        (it) => tags[it].text === 'تناوب جمع‌آوری (ورود داده)'
      );
      toSave.data.identifierValues = { ...toSave.data.identifierValues };
      if (fillPeriodInIds) {
        const val = periodType.find((it) => it.value === selectedPeriod)?.label;
        setIdValue(fillPeriodInIds, val, toSave);
      }
      const analysePeriodInIds = idParentList.find(
        (it) => tags[it].text === 'تناوب تحلیل'
      );
      if (analysePeriodInIds) {
        const val = periodType.find(
          (it) => it.value === selectedAnalysedPeriodType
        )?.label;
        setIdValue(analysePeriodInIds, val, toSave);
      }
      // const indicatorUnit = idParentList.find(
      //   (it) => tags[it].text === 'واحد اندازه گیری شاخص'
      // );
      // if (indicatorUnit) {
      //   const val = getEvaluatedMetric(
      //     indicatorState.numeratorUnit,
      //     indicatorState.denominatorUnit
      //   );
      //   setIdTagValue(indicatorUnit, val, toSave);
      // }
      await updateItem(toSave.data);
      if (oddIndicator) return;
      const multichoiceQuestionId =
        multipleQuestionTag?.id || Math.ceil(Math.random() * 100000000000);
      const indicatorQuestionId =
        indicatorQuestion?.id || Math.ceil(Math.random() * 100000000000);
      if (indicatorQuestion) {
        await appDispatch(
          updateQuestion({
            questionData: { ...indicatorQuestion, ...indicatorState },
            providerSurveyData: item.data as ISurveyItem,
          })
        );
      } else {
        await appDispatch(
          addQuestion({
            questionData: {
              ...({
                id: indicatorQuestionId,
                order: 1000,
                weight: 1,
                answerRequired: true,
                type: 'indicator',
              } as IQuestionsView),
              ...indicatorState,
            },
            providerSurveyData: item.data as ISurveyItem,
            keepQuestionId: true,
          })
        );
        setIn;
      }
      // multiple choice quesiton
      if (
        !initialMultipleChoiceTagDeletedStatus &&
        isMultipleChoiceTagDeleted
      ) {
        // Delete
        await appDispatch(
          updateQuestion({
            questionData: {
              ...multipleQuestionTag,
              deleted: true,
            } as IQuestionsView,
            providerSurveyData: toSave.data as ISurveyItem,
          })
        );
      } else if (!isMultipleChoiceTagDeleted) {
        // Add
        if (multipleQuestionTag) {
          await appDispatch(
            updateQuestion({
              questionData: {
                ...multipleQuestionTag,
                tagChoices: subTags,
                parentTagChoices: selectedParentTagIdList,
                excludedTagChoices: excludeTagChoice,
                deleted: false,
              } as IQuestionsView,
              providerSurveyData: toSave.data as ISurveyItem,
            })
          );
        } else {
          const added = await appDispatch(
            addQuestion({
              questionData: {
                ...({
                  id: multichoiceQuestionId,
                  title: i18n.t('sectionOrUnit', { ns: 'survey' }),
                  order: 1,
                  weight: 1,
                  answerRequired: true,
                  distinctive: true,
                  useTags: true,
                  type: 'multipleChoice',
                  tagChoices: subTags,
                  parentTagChoices: selectedParentTagIdList,
                  excludedTagChoices: excludeTagChoice,
                } as IQuestionsView),
              },
              keepQuestionId: true,
              providerSurveyData: toSave.data as ISurveyItem,
            })
          );
          setMultipleQuestionTag(added.newQuestion);
        }
      } else {
        // do nothing
      }

      // Analyse section
      if (analyseItem) {
        const finalTagChoices =
          selectedAnalysedOption === 'both'
            ? [hospitalId, ...subTags]
            : selectedAnalysedOption === 'default'
              ? [...subTags]
              : [hospitalId];
        const updatedParentTask =
          selectedAnalysedOption === 'both' ||
          selectedAnalysedOption === 'default'
            ? selectedParentTagIdList
            : [];

        if (analyseMultipleQuestion) {
          // update it
          await appDispatch(
            updateQuestion({
              questionData: {
                ...analyseMultipleQuestion,
                tagChoices: finalTagChoices,
                parentTagChoices: updatedParentTask,
                excludedTagChoices: excludeTagChoice,
                deleted: false,
              } as IQuestionsView,
              providerSurveyData: analyseItem.data as ISurveyItem,
            })
          );
        } else if (selectedAnalysedOption !== 'total') {
          await appDispatch(
            addQuestion({
              questionData: {
                ...({
                  id: multichoiceQuestionId,
                  title: i18n.t('sectionOrUnit', { ns: 'survey' }),
                  order: 1,
                  weight: 1,
                  answerRequired: true,
                  distinctive: true,
                  useTags: true,
                  type: 'multipleChoice',
                  tagChoices: finalTagChoices,
                  parentTagChoices: updatedParentTask,
                  excludedTagChoices: excludeTagChoice,
                  fillPermissionSchemeList:
                    multipleQuestionTag?.fillPermissionSchemeList || [],
                  reportPermissionSchemeList:
                    multipleQuestionTag?.reportPermissionSchemeList || [],
                } as IQuestionsView),
              },
              keepQuestionId: true,
              providerSurveyData: analyseItem.data as ISurveyItem,
            })
          );
        }
      } else if (templateSurvey) {
        await createAnalyseSurvey();
      }
      // console.log(!initialOtherSplitStatus, isOtherSplitDeleted);
      if (!initialOtherSplitQuestionStatus && isOtherSplitDeleted) {
        // Delete
        await appDispatch(
          updateQuestion({
            questionData: {
              ...otherSpitQuestion,
              deleted: true,
            },
            providerSurveyData: toSave.data as ISurveyItem,
          })
        );
      } else if (!isOtherSplitDeleted) {
        if (otherSpitQuestion) {
          // if exists --> deleted: change to false and add these options --> if exists update options and title
          await appDispatch(
            updateQuestion({
              questionData: {
                ...otherSpitQuestion,
                deleted: false,
                title: otherSplitState.title,
                choiceList: otherSplitState.choices,
              } as IQuestionsView,
              providerSurveyData: toSave.data as ISurveyItem,
            })
          );
        } else {
          // if doesn't exist make it and so then add these new information
          await appDispatch(
            addQuestion({
              questionData: {
                id: Math.ceil(Math.random() * 100000000000000),
                order: 100,
                weight: 1,
                distinctive: true,
                type: 'multipleChoice',
                useTags: false,
                title: otherSplitState.title,
                choiceList: otherSplitState.choices,
              } as IQuestionsView,
              providerSurveyData: toSave.data as ISurveyItem,
            })
          );
        }
      }
      let fields: Field[] = cloneDeep(
        survey?.data.versions?.[viewingVersion].fields
      );
      // set prevent draft
      const selectedFieldIndex = fields.findIndex(
        (field) => field.name === 'conf_allowDraft'
      );
      if (selectedFieldIndex >= 0) {
        fields[selectedFieldIndex].value = 'false';
      } else {
        fields = [
          ...fields,
          { name: 'conf_allowDraft', value: 'false' } as Field,
        ];
      }

      //
      if (isMultipleChoiceTagDeleted) {
        fields = fields.filter((it) => it.name !== 'conf_skipSurveyTag');
      } else {
        const selectedFieldIndex = fields.findIndex(
          (field) => field.name === 'conf_skipSurveyTag'
        );
        if (selectedFieldIndex >= 0) {
          fields[selectedFieldIndex].value = 'true';
        } else {
          fields = [
            ...fields,
            { name: 'conf_skipSurveyTag', value: 'true' } as Field,
          ];
        }
      }

      await appDispatch(
        updateSurvey({
          surveyData: {
            ...(survey?.data.versions?.[viewingVersion] as ISurveyView),
            periodType: selectedPeriod as PeriodType,
            fields: fields,
            autoLock: true,
            keepUnique: true,
            trackResponseChanges: true,
            uniquePerTime: true,
            questionMap: {},
            questionList: [],
            name: indicatorQuestion?.title || survey.data.name,
          },
          providerSurveyItemData: toSave.data as ISurveyItem,
        })
      );

      if (analyseSurvey) {
        await appDispatch(
          updateSurvey({
            surveyData: {
              ...(analyseSurvey.data.versions?.[viewingVersion] as ISurveyView),
              periodType: selectedAnalysedPeriodType as PeriodType,
            },
            providerSurveyItemData: analyseItem.data as ISurveyItem,
          })
        );
      }

      await appDispatch(
        generateReports({
          questionId: indicatorQuestionId as number,
          config: {},
          surveyToken: (toSave.data as ISurveyItem).editToken,
          updateExisting: true,
        })
      );
      if (analyseItem)
        await appDispatch(
          requireSurvey({
            providerSurveyItemData: analyseItem.data,
            forceReload: true,
          })
        );
      await appDispatch(
        requireSurvey({
          providerSurveyItemData: item.data as ISurveyItem,
          forceReload: true,
        })
      );
      notify(i18n.t('savedSuccessfully', { ns: 'survey' }));
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  const [filteredSubTags, setFilteredSubTags] = useState<number[]>(() => {
    const itemTags = item.data.tags.reduce((acc: number[], tag) => {
      acc.push(+tag.id);
      return acc;
    }, []);
    return (
      getTags(tags, true, hospitalChildId)
        .map((it) => +it.value)
        ?.filter((tagId) => itemTags.includes(tagId)) || []
    );
  });

  const editable = item.data.editable && !readonly;
  const validPeriodTypes = periodType.filter((it) => it.value !== 'none');
  const side = languageDirection() === 'rtl' ? 'left' : 'right';

  const handleFindPreDefinedFields = (parentTagId: number) => {
    const parentTagName = Object.values(tags)
      .find((tag) => tag.id === parentTagId)
      ?.text.trim();
    const childrenTags = Object.values(tags).filter(
      (el) => el.parentId === parentTagId
    );

    if (childrenTags.length === 0) {
      const item = indicatorQuestion?.fields?.find(
        (item) => item.name.trim() === parentTagName
      );
      return (
        item?.valueList?.join(', ') ?? item?.value?.replace(/\n/g, ' - ') ?? ''
      );
    }

    const valueList = indicatorQuestion?.fields?.find(
      (el) => el.name === parentTagName
    )?.valueList;

    handleFieldsWhichWasUnableToImport(
      parentTagId,
      parentTagName as string,
      childrenTags
        .filter((item) => valueList?.includes(item.text))
        .map((item) => item.text.trim())
    );

    return childrenTags
      .filter((item) => valueList?.includes(item.text))
      .reduce((acc: IOption[], item) => {
        acc.push({
          label: item.text,
          value: item.id,
        });
        return acc;
      }, []);
  };

  const handleFieldsWhichWasUnableToImport = (
    parentTagId: number,
    parentTagName: string,
    childrenTags: string[]
  ) => {
    const unableToImportTag = indicatorQuestion?.fields
      ?.find((el) => el.name === parentTagName)
      ?.valueList?.filter((value) => !childrenTags.includes(value))
      ?.join(', ');

    if (unableToImportTag !== '') {
      setUnableToImport((prevVal) => ({
        ...prevVal,
        [parentTagId]: `${t('itemsNotFound')} (${unableToImportTag as string})`,
      }));
    }
  };

  const handleImportValuesFromOldIdentification = () => {
    idParentList.map((tagId) => {
      let itemValues = idValueList[tagId]
        ? currentTags
            .filter((it) => idValueList[tagId].includes(it.id))
            .map((it) => ({ value: it.id, label: it.text }))
        : [];

      if (itemValues.length === 0) {
        itemValues = handleFindPreDefinedFields(tagId as number) as IOption[];
      }

      if (itemValues?.length >= 1 && Array.isArray(itemValues)) {
        const toAdd = new Set(
          itemValues.reduce((acc: number[], item) => {
            acc.push(Number(item.value));
            return acc;
          }, [])
        );
        const toRemove = new Set([]);
        updateTag(toAdd, toRemove);
      } else if (!currentIdValues[tagId] && typeof itemValues === 'string') {
        setCurrentIdValues((prevVal) => {
          return {
            ...prevVal,
            [tagId]: handleFindPreDefinedFields(tagId as number),
          };
        });
      }
    });
  };

  let selectedSetionIDFromAllTags = [...subTags];
  selectedSetionIDFromAllTags = [
    ...selectedSetionIDFromAllTags,
    ...Object.values(tags)
      .filter((tag) => selectedParentTagIdList.includes(tag.parentId as number))
      .reduce((acc: number[], tag) => {
        acc.push(+tag.id);
        return acc;
      }, []),
  ];
  selectedSetionIDFromAllTags = Array.from(
    new Set(selectedSetionIDFromAllTags)
  );
  selectedSetionIDFromAllTags = selectedSetionIDFromAllTags.filter(
    (tagId) => !excludeTagChoice.includes(tagId)
  );

  const selectedSectionNameFromAllTags = Object.values(tags)
    .filter((item: ITagView) => selectedSetionIDFromAllTags.includes(+item.id))
    .reduce((acc: string[], selectedSection) => {
      acc.push(selectedSection.text);
      return acc;
    }, []);

  return (
    <IDContainer
      style={{
        margin: ' 20px auto',
        maxWidth: '1000px',
        position: 'relative',
        display: 'block',
      }}
    >
      <form
        style={{
          flexGrow: 1,
          overflow: 'scroll',
          height: 'calc(100vh - 160px)',
          paddingInlineEnd: editable ? '100px' : undefined,
        }}
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        {editable &&
          (oddIndicator ? (
            <p>{i18n.t('editableOnlyBySupport', { ns: 'survey' }) as string}</p>
          ) : (
            <Collapsible
              defaultOpen={true}
              header={i18n.t('settings', { ns: 'survey' })}
              style={{ overflow: 'visible' }}
              showOverflow={true}
            >
              <div>
                <div
                  style={{
                    border: `1px solid ${lightGray}`,
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    margin: '1rem 0',
                  }}
                >
                  <div className="flex align-center flex-col py-2">
                    <div className="w-full">
                      <h5>
                        {i18n.t('initialConfigs', { ns: 'survey' }) as string}
                      </h5>
                      <Input
                        id={
                          indicatorQuestion?.id.toString() ||
                          Math.random().toString()
                        }
                        type="text"
                        value={indicatorState.title || ''}
                        label={
                          i18n.t('indicatorQuestion', {
                            ns: 'survey',
                          }) as string
                        }
                        onChange={(e) => {
                          dispatch({ type: 'title', payload: e.target.value });
                        }}
                        required={true}
                      />
                    </div>
                    <div className="flex w-full gap-3">
                      <div className="w-full">
                        <Input
                          id={
                            indicatorQuestion?.id.toString() + 'numerator' ||
                            Math.random().toString()
                          }
                          type="text"
                          value={indicatorState.numerator || ''}
                          onChange={(e) => {
                            dispatch({
                              type: 'numerator',
                              payload: e.target.value,
                            });
                          }}
                          required={true}
                          placeholder={i18n.t('required', { ns: 'survey' })}
                          label={
                            i18n.t('numeratorQuestion', {
                              ns: 'survey',
                            }) as string
                          }
                        />
                      </div>
                      <div className="w-full">
                        <Select
                          values={
                            indicatorState.numeratorUnit
                              ? [
                                  {
                                    value: indicatorState.numeratorUnit,
                                    label: indicatorState.numeratorUnit,
                                  },
                                ]
                              : []
                          }
                          options={unitOptions}
                          onChangeValues={(units) => {
                            dispatch({
                              type: 'numeratorUnit',
                              payload: units[0]?.value?.toString(),
                            });
                          }}
                          isRequired={true}
                          allowRemove={false}
                          label={
                            i18n.t('numeratorUnit', { ns: 'survey' }) as string
                          }
                        />
                      </div>
                    </div>
                    <div className="flex w-full gap-3">
                      <div className="w-full">
                        <Input
                          id={
                            indicatorQuestion?.id.toString() + 'denominator' ||
                            Math.random().toString()
                          }
                          type="text"
                          value={indicatorState.denominator || ''}
                          onChange={(e) => {
                            dispatch({
                              type: 'denominator',
                              payload: e.target.value,
                            });
                          }}
                          required={!!indicatorState.denominatorUnit}
                          placeholder={i18n.t('withoutDenominator', {
                            ns: 'survey',
                          })}
                          label={
                            i18n.t('denominatorQuestion', {
                              ns: 'survey',
                            }) as string
                          }
                        />
                      </div>
                      <div className="w-full">
                        <Select
                          values={
                            indicatorState.denominatorUnit
                              ? [
                                  {
                                    value: indicatorState.denominatorUnit,
                                    label: indicatorState.denominatorUnit,
                                  },
                                ]
                              : []
                          }
                          options={unitOptions}
                          onChangeValues={(units) => {
                            dispatch({
                              type: 'denominatorUnit',
                              payload: units[0]?.value?.toString(),
                            });
                          }}
                          isRequired={!!indicatorState.denominator}
                          allowRemove={!indicatorState.denominator}
                          label={
                            i18n.t('denominatorUnit', {
                              ns: 'survey',
                            }) as string
                          }
                        />
                      </div>
                    </div>
                    {indicatorQuestion &&
                      !!indicatorState.denominator !==
                        !!indicatorQuestion?.denominator && (
                        <p style={{ color: orange }}>
                          {
                            i18n.t('changeTypeWarning', {
                              ns: 'survey',
                            }) as string
                          }
                        </p>
                      )}
                    <p>
                      {indicatorState.numeratorUnit ===
                      indicatorState.denominatorUnit ? (
                        <Select
                          values={
                            indicatorState?.unit === 'fraction'
                              ? [indicatorUnitOptions[1]]
                              : [indicatorUnitOptions[0]]
                          }
                          options={indicatorUnitOptions}
                          onChangeValues={(units) => {
                            dispatch({
                              type: 'unit',
                              payload: units[0]?.value?.toString(),
                            });
                          }}
                          label={messages.evaluatedMetric('')}
                          allowRemove={false}
                        />
                      ) : (
                        messages.evaluatedMetric(
                          getEvaluatedMetric(
                            indicatorState.numeratorUnit,
                            indicatorState.denominatorUnit
                          )
                        )
                      )}
                    </p>
                  </div>
                  <Select
                    options={validPeriodTypes}
                    isRequired={true}
                    allowRemove={false}
                    disabled={readonly}
                    values={validPeriodTypes.filter(
                      (item: IOption) =>
                        item.value.toString() === selectedPeriod
                    )}
                    onChangeValues={(fieldOption) => {
                      setSelectedPeriod(fieldOption[0].value.toString());
                    }}
                    placeholder={i18n.t('pleaseSelectAValue', { ns: 'survey' })}
                    label={i18n.t('periodType', { ns: 'survey' }) as string}
                    containerStyle={{ marginBottom: '1rem' }}
                  />
                </div>
                {/* */}
                <div
                  style={{
                    border: `1px solid ${lightGray}`,
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    margin: '1rem 0',
                  }}
                >
                  <div>
                    <h5>
                      {
                        i18n.t('destrictConfiguration', {
                          ns: 'survey',
                        }) as string
                      }
                    </h5>
                    <Input
                      wrapperStyle={{ flex: 1 }}
                      id={Math.random().toString()}
                      type="checkbox"
                      checked={!isMultipleChoiceTagDeleted}
                      label={
                        i18n.t('divideSections', { ns: 'survey' }) as string
                      }
                      onChange={(e) => {
                        setIsMultipleChoiceTagDeleted(!e.target.checked);
                        if (!e.target.checked)
                          setSelectedAnalysedOption('total');
                        else
                          setSelectedAnalysedOption(
                            getCurrentAnalyseSplitValue()
                          );
                      }}
                    />
                  </div>
                  {isMultipleChoiceTagDeleted ? (
                    <>
                      <p
                        style={
                          side === 'left'
                            ? { textAlign: 'right' }
                            : { textAlign: 'left' }
                        }
                      >
                        {
                          i18n.t('districtSplitHint', {
                            ns: 'survey',
                          }) as string
                        }
                      </p>
                      <Select
                        label={
                          i18n.t('relatedDistricts', { ns: 'survey' }) as string
                        }
                        options={
                          tags ? getTags(tags, true, hospitalChildId) : []
                        }
                        multiChoice={true}
                        isRequired={true}
                        values={
                          filteredSubTags
                            ? convertTagIDs(filteredSubTags, tags)
                            : []
                        }
                        onChangeValues={(tags) => {
                          const updatedTagIdList = tags.reduce(
                            (tagIds: number[], tagId) => {
                              tagIds.push(+tagId.value);
                              return tagIds;
                            },
                            []
                          );
                          setFilteredSubTags(updatedTagIdList);
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Select
                        label={
                          i18n.t('allSubTagsOf', { ns: 'survey' }) as string
                        }
                        onChangeValues={(tagsOption) => {
                          setSelectedParentTagIdList(
                            tagsOption.reduce((list: number[], tag) => {
                              list.push(+tag.value);
                              return list;
                            }, [])
                          );
                        }}
                        options={tags ? getTags(tags, true, [hospitalId]) : []}
                        values={Object.keys(tags)
                          .filter((tagId) =>
                            selectedParentTagIdList.includes(+tagId)
                          )
                          .map((tagId) => {
                            return {
                              label: tags[tagId].text,
                              value: tags[tagId].id,
                            };
                          })}
                        placeholder={i18n.t('selectTag', { ns: 'survey' })}
                        multiChoice={true}
                      />
                      <Select
                        values={subTags ? convertTagIDs(subTags, tags) : []}
                        options={
                          tags ? getTags(tags, true, hospitalChildId) : []
                        }
                        multiChoice={true}
                        placeholder={i18n.t('selectTag', { ns: 'survey' })}
                        onChangeValues={(tags) => {
                          const updatedTagIdList = tags.reduce(
                            (tagIds: number[], tagId) => {
                              tagIds.push(+tagId.value);
                              return tagIds;
                            },
                            []
                          );
                          setSubTags(updatedTagIdList);
                        }}
                        isRequired={!selectedParentTagIdList.length}
                        label={i18n.t('allTags', { ns: 'survey' }) as string}
                      />
                      <Select
                        onChangeValues={(tags: IOption[]) => {
                          const excludedTags = tags.reduce(
                            (tagIDs: number[], tag) => {
                              tagIDs.push(+tag.value);
                              return tagIDs;
                            },
                            []
                          );
                          setExcludeTagChoice(excludedTags);
                        }}
                        options={
                          tags
                            ? getTags(tags, true, selectedParentTagIdList)
                            : []
                        }
                        values={
                          excludeTagChoice
                            ? convertTagIDs(excludeTagChoice, tags)
                            : []
                        }
                        multiChoice={true}
                        label={
                          i18n.t('excludedTags', { ns: 'survey' }) as string
                        }
                      />
                      <>
                        <div
                          style={{
                            fontWeight: 'bold',
                            padding: '0.5rem 0',
                            fontSize: '1rem',
                            paddingInlineStart: '0.25rem',
                            flexWrap: 'wrap',
                          }}
                        >
                          {
                            i18n.t('finalSelectedTags', {
                              ns: 'survey',
                            }) as string
                          }
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          {selectedSectionNameFromAllTags.map((item, ind) => (
                            <SelectedSectionItem key={ind}>
                              {item}
                            </SelectedSectionItem>
                          ))}
                        </div>
                      </>
                    </>
                  )}
                </div>
                <div
                  style={{
                    border: `1px solid ${lightGray}`,
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                  }}
                >
                  <Input
                    id={Math.random().toString()}
                    type="checkbox"
                    checked={!isOtherSplitDeleted}
                    label={i18n.t('otherSplit', { ns: 'survey' }) as string}
                    onChange={(e) => {
                      setIsOtherSplitDeleted(!e.target.checked);
                    }}
                  />
                  {!isOtherSplitDeleted && (
                    <>
                      <div>
                        <Input
                          id="otherSplit"
                          type="text"
                          label={
                            i18n.t('otherSplitTitle', {
                              ns: 'survey',
                            }) as string
                          }
                          value={otherSplitState?.title || ''}
                          onChange={(e) =>
                            otherSplitDispatch({
                              type: 'title',
                              payload: { value: e.target.value.toString() },
                            })
                          }
                          required={true}
                        />
                        <div className="flex items-center gap-2 my-1">
                          <Icon
                            type="plus-circle"
                            style={{ cursor: 'pointer', fontSize: '1.5rem' }}
                            onClick={() =>
                              otherSplitDispatch({
                                type: 'add',
                                payload: {
                                  value: uuid(),
                                },
                              })
                            }
                          />
                          <p
                            className="w-full m-0"
                            style={
                              side === 'left'
                                ? { textAlign: 'right' }
                                : { textAlign: 'left' }
                            }
                          >
                            {i18n.t('addOption', { ns: 'survey' }) as string}
                          </p>
                        </div>
                        {/* it Seems it is useless */}
                        {/*{!otherSplitState.choices.length && (*/}
                        {/*  <input*/}
                        {/*    type="text"*/}
                        {/*    required={true}*/}
                        {/*    style={{ height: 0, width: '20px', margin: 0 }}*/}
                        {/*    aria-disabled={true}*/}
                        {/*  />*/}
                        {/*)}*/}
                      </div>

                      {otherSplitState.choices.map((choice: IChoiceView) => {
                        return (
                          <div
                            key={choice.value.toString()}
                            className="flex items-center gap-3"
                          >
                            <Icon
                              type="x-circle"
                              onClick={() =>
                                otherSplitDispatch({
                                  type: 'delete',
                                  payload: { value: choice.value.toString() },
                                })
                              }
                              style={{ cursor: 'pointer', fontSize: '1.5rem' }}
                            />
                            <Input
                              id={`otherSplitChoice-${choice.value}`}
                              type="text"
                              value={choice.text}
                              onChange={(event) =>
                                otherSplitDispatch({
                                  type: 'update',
                                  payload: {
                                    value: choice.value.toString(),
                                    text: event.target.value,
                                  },
                                })
                              }
                              wrapperStyle={{ width: '100%' }}
                              required={true}
                            />
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
                <div
                  style={{
                    border: `1px solid ${lightGray}`,
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    margin: '1rem 0',
                  }}
                >
                  <h5>
                    {i18n.t('analyseSetting', { ns: 'survey' }) as string}
                  </h5>
                  {!isMultipleChoiceTagDeleted && (
                    <Select
                      allowRemove={false}
                      onChangeValues={(values) => {
                        setSelectedAnalysedOption(
                          values[0].value.toString() as any
                        );
                      }}
                      options={questionIncludeOptions}
                      values={questionIncludeOptions.filter(
                        (it) => it.value === selectedAnalysedOption
                      )}
                      label={i18n.t('analyseSplit', { ns: 'survey' }) as string}
                    />
                  )}
                  <Select
                    options={validPeriodTypes}
                    isRequired={true}
                    allowRemove={false}
                    disabled={readonly}
                    values={validPeriodTypes.filter(
                      (item: IOption) =>
                        item.value.toString() === selectedAnalysedPeriodType
                    )}
                    onChangeValues={(fieldOption) => {
                      setSelectedAnalysedPeriodType(
                        fieldOption[0].value.toString()
                      );
                    }}
                    placeholder={i18n.t('analysedPeriodType', { ns: 'survey' })}
                    label={
                      i18n.t('analysedPeriodType', { ns: 'survey' }) as string
                    }
                  />
                </div>
              </div>
            </Collapsible>
          ))}
        <table>
          <tbody>
            {idParentList.map((tagId) => {
              const itemValues = idValueList[tagId]
                ? currentTags
                    .filter((it) => idValueList[tagId].includes(it.id))
                    .map((it) => ({ value: it.id, label: it.text }))
                : [];

              return (
                <tr key={tagId}>
                  <td
                    style={{
                      width: '200px',
                      padding: 0,
                      textAlign: side === 'right' ? 'left' : 'right',
                    }}
                  >
                    {tags[tagId].text}
                  </td>
                  <td style={{ padding: 0 }}>
                    {idValueList[tagId] ? (
                      <Select
                        label={unableToImport?.[tagId] || ''}
                        onChangeValues={handleUpdateTag(itemValues)}
                        values={itemValues}
                        options={idValueList[tagId].map((it: number) => ({
                          value: it,
                          label: tags[it].text,
                        }))}
                        multiChoice={true}
                        disabled={!editable}
                      />
                    ) : (
                      <Input
                        id={tagId.toString()}
                        type="text"
                        value={currentIdValues[tagId]}
                        onChange={(it) =>
                          setCurrentIdValues({
                            ...currentIdValues,
                            [tagId]: it.target.value,
                          })
                        }
                        contentEditable={editable}
                        labelStyle={{ color: red }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* --- */}
        {editable && (
          <Button
            loading={saving}
            type="submit"
            color={'green'}
            style={{
              position: 'absolute',
              top: '17px',
              [side]: '25px',
              width: '85px',
            }}
          >
            {i18n.t('save', { ns: 'survey' }) as string}
          </Button>
        )}
      </form>
      <Button
        loading={exporting}
        type="button"
        color={'green'}
        onClick={handleExportExcel}
        style={{
          position: 'absolute',
          top: '60px',
          [side]: '25px',
          width: '85px',
        }}
      >
        {i18n.t('ExportExcel', { ns: 'survey' }) as string}
      </Button>

      {indicatorQuestion?.fields?.length !== undefined &&
        indicatorQuestion?.fields?.length > 0 && (
          <Button
            type="button"
            color={'black'}
            onClick={handleImportValuesFromOldIdentification}
            style={{
              position: 'absolute',
              top: '120px',
              [side]: '25px',
              width: '85px',
            }}
          >
            {i18n.t('fetchFromOldID', { ns: 'survey' }) as string}
          </Button>
        )}
    </IDContainer>
  );
};

// TODO: We must use bindActionCreators here
const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  ownProps
) => {
  const templateSurvey =
    ownProps.templateItem &&
    state.survey.data[ownProps.templateItem.data.surveyId]?.data;
  const providerId = ownProps.item.data.providerId;
  const tags = state.providers.tags[providerId]?.data;
  return { tags: tags || {}, templateSurvey };
};

export default connect(mapStateToProps, null)(Identification as any);
